// frontend/app/components/lessonPlans/authoring/loopBlockSchemas.js
//
// The five block types the Saras Learning Loop adds on top of the shipped
// thirteen. Fields follow Appendix A of the Learning Loop design exactly — the
// runtime keys student responses on `(step_key, block_id)` and reads these
// field names, so a rename here is a silent content loss there.
//
// Same declaration shape as blockSchemas.js: one entry, no per-type form.

import {
  aiFeedbackField,
  conceptField,
  EVIDENCE_WEIGHT,
  FIELD,
  placeholderField,
} from "./blockFields";

/**
 * The document's own two prompts. An author can add, rename or reorder them,
 * but a new reflection starts as the reflection the pedagogy describes rather
 * than as an empty list nobody knows how to fill.
 */
export const DEFAULT_REFLECTION_PROMPTS = [
  { key: "before", label: "Before today's lesson I thought…" },
  { key: "now", label: "Now I understand…" },
];

/** On by default; cleared to leave it out (Appendix A: "set null to omit"). */
export const DEFAULT_CONFUSION_PROMPT = "Anything still confusing?";

export const LOOP_BLOCK_SCHEMAS = [
  {
    type: "exploration_probe",
    label: "Exploration probe",
    icon: "magnify-scan",
    group: "written",
    phase: "explore",
    strictPhase: true,
    evidenceWeight: EVIDENCE_WEIGHT.PROBE,
    fields: [
      {
        key: "prompt",
        label: "Question",
        kind: FIELD.LONG_TEXT,
        required: true,
        placeholder: "What changed? What stayed the same?",
      },
      {
        key: "hint",
        label: "Nudge if they are stuck",
        kind: FIELD.TEXT,
        placeholder: "Look at the two drones' routes.",
      },
      aiFeedbackField,
      conceptField,
    ],
  },
  {
    type: "explanation",
    label: "Explanation",
    icon: "comment-text-outline",
    group: "written",
    phase: "explain",
    strictPhase: true,
    evidenceWeight: EVIDENCE_WEIGHT.EXPLANATION,
    fields: [
      {
        key: "prompt",
        label: "What to explain",
        kind: FIELD.LONG_TEXT,
        required: true,
        placeholder: "Explain to your classmate how to calculate speed.",
      },
      { ...placeholderField, placeholder: "Write your explanation…" },
      {
        key: "min_words",
        label: "Fewest words accepted",
        kind: FIELD.NUMBER,
        placeholder: "25",
        hint: "Leave blank to accept any length.",
      },
      {
        key: "rubric",
        label: "What a good explanation contains",
        kind: FIELD.STRING_LIST,
        ordered: true,
        placeholder: "States that speed is distance divided by time",
        addLabel: "Add criterion",
        hint: "Saras scores against these, in this order. Students never see them.",
      },
      aiFeedbackField,
      conceptField,
    ],
  },
  {
    type: "reflection",
    label: "Reflection",
    icon: "mirror",
    group: "growth",
    phase: "reflect",
    strictPhase: true,
    evidenceWeight: EVIDENCE_WEIGHT.NONE,
    fields: [
      {
        key: "prompts",
        label: "Sentence starters",
        kind: FIELD.PROMPT_LIST,
        required: true,
        defaultValue: DEFAULT_REFLECTION_PROMPTS,
        addLabel: "Add prompt",
        hint: "One box per starter, in this order. Four at most.",
        maxItems: 4,
      },
      {
        key: "confusion_prompt",
        label: "Still-confusing box",
        kind: FIELD.TEXT,
        defaultValue: DEFAULT_CONFUSION_PROMPT,
        hint: "Clear it to leave the box out. This is the column Insights reads.",
      },
      conceptField,
    ],
  },
  {
    type: "mastery_map",
    label: "Mastery map",
    icon: "chart-timeline-variant",
    group: "growth",
    phase: "grow",
    strictPhase: true,
    evidenceWeight: EVIDENCE_WEIGHT.NONE,
    fields: [
      { key: "title", label: "Heading", kind: FIELD.TEXT, defaultValue: "Where you are now" },
      {
        key: "scope",
        label: "Which concepts",
        kind: FIELD.CHIPS,
        defaultValue: "lesson",
        options: [
          { value: "lesson", label: "This lesson", accent: "#0891b2" },
          { value: "journey", label: "The whole journey", accent: "#65a30d" },
        ],
        hint: "Journey scope only fills in once the lesson sits inside one.",
      },
      {
        key: "show_next_steps",
        label: "Show what to do next",
        kind: FIELD.TOGGLE,
        defaultValue: true,
        hint: "Renders the band's own transition message under each bar.",
      },
    ],
  },
  {
    type: "next_challenge",
    label: "Next challenge",
    icon: "arrow-right-circle-outline",
    group: "growth",
    phase: "grow",
    strictPhase: true,
    evidenceWeight: EVIDENCE_WEIGHT.NONE,
    fields: [
      {
        key: "prompt",
        // Not this lesson's question — the one the next Loop opens on.
        label: "The next loop's question",
        kind: FIELD.LONG_TEXT,
        required: true,
        placeholder: "Why isn't speed enough?",
        hint: "Students answer it cold, before the next lesson teaches anything.",
      },
      {
        key: "next_loop_hint",
        label: "Where it leads",
        kind: FIELD.TEXT,
        placeholder: "Loop 2 · Why Isn't Speed Enough?",
      },
      { ...placeholderField, placeholder: "Your first thoughts…" },
    ],
  },
];
